import type { Book, User } from "@/schema"

// This is the sqid-encoded value of ALL_BOOKS_ID (9_999_999_999_999) from the backend
// If the sqid configuration changes, this value must be updated
export const ALL_BOOKS_SQID = "XlmBcbX57D"

export const ALL_BOOKS_DISABLED_TOOLTIP = "Switch to a specific book to use this"

export function isAllBooksId(bookId: string | undefined): boolean {
  return bookId === ALL_BOOKS_SQID
}

type DefaultBookResolvable = Pick<
  User,
  "default_to_all_books" | "default_book" | "principal_books" | "books" | "sidebar_book_ids"
>

// The book a user should land on. All Books is only a valid landing spot for
// users with more than one accessible book — a one-book user defaulted to All
// Books would otherwise be stranded (no switcher, disabled book-only nav).
export function defaultLandingBookId(user?: DefaultBookResolvable | null): string | undefined {
  if (!user) return undefined
  if (user.default_to_all_books && user.books.length > 1) return ALL_BOOKS_SQID
  const accessibleBookIds = new Set(user.books.map((book) => book.id))
  const firstOrderedBookId = user.sidebar_book_ids?.find((id) => accessibleBookIds.has(id))
  return user.default_book?.id ?? firstOrderedBookId ?? user.principal_books?.[0]?.id ?? user.books?.[0]?.id
}

// The concrete book a cross-book flow (like installing shared items) should start in: the landing
// book unless that is All Books, which cannot receive book-scoped records.
export function defaultConcreteBookId(user?: DefaultBookResolvable | null): string | undefined {
  return defaultLandingBookId(user ? { ...user, default_to_all_books: false } : user)
}

export function bookShortName(book: Book, allBooks: Book[]): string {
  if (book.name && book.name.trim() !== "") return book.name.trim()
  const firstName = book.principal.full_name.split(" ")[0]
  const hasDuplicate = allBooks.some(
    (other) => other.id !== book.id && other.principal.full_name.split(" ")[0] === firstName
  )
  const lastInitial = book.principal.full_name.split(" ")[1]?.[0]
  const label = hasDuplicate && lastInitial ? `${firstName} ${lastInitial}.` : firstName
  return `${label}'s book`
}
